import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import { LangContext } from '../App';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// ── Translations ──────────────────────────────────────────────────────────────
const T = {
  fr: {
    title: "Fil d'activité",
    sousTitre: 'Les dernières actions des membres que vous suivez.',
    paywallTitre: 'Fonctionnalité Premium',
    paywallDesc:
      "Le fil d'activité et le partage social sont réservés aux membres Premium. Passez au level supérieur pour suivre d'autres chasseurs et voir leur activité.",
    paywallBtn: 'Voir mes crédits et passer Premium',
    membres: 'Membres suivis',
    aucunSuivi: "Vous ne suivez personne pour l'instant.",
    chercherMembre: 'Suivre un membre (ID utilisateur)',
    suivre: 'Suivre',
    enCours: 'En cours…',
    dejasuivi: 'Suivi',
    seDesabonner: 'Se désabonner',
    feed: 'Activité récente',
    aucunEvent: 'Aucune activité récente. Commencez par suivre des membres !',
    chargement: 'Chargement…',
    erreur: 'Erreur lors du chargement.',
    precedent: '← Précédent',
    suivant: 'Suivant →',
    page: 'Page',
    eventTypes: {
      observation_published: 'a publié une observation',
      observation_validated: 'a validé une observation',
      hunt_published: 'a partagé une sortie',
    },
    species: 'Espèce',
    category: 'Catégorie',
    categories: {
      migrateur: 'Migrateur',
      sedentaire: 'Sédentaire',
      spot_peche: 'Pêche',
      etat_zone: 'État zone',
    },
    erreurSuivi: 'Impossible de suivre cet utilisateur.',
    erreurDesuivi: 'Impossible de se désabonner.',
    idInvalide: 'Entrez un ID numérique valide.',
  },
  en: {
    title: 'Activity Feed',
    sousTitre: 'Latest actions from members you follow.',
    paywallTitre: 'Premium Feature',
    paywallDesc:
      'The activity feed and social sharing are reserved for Premium members. Upgrade to follow other hunters and see their activity.',
    paywallBtn: 'View my credits and go Premium',
    membres: 'Followed members',
    aucunSuivi: 'You are not following anyone yet.',
    chercherMembre: 'Follow a member (user ID)',
    suivre: 'Follow',
    enCours: 'Loading…',
    dejasuivi: 'Following',
    seDesabonner: 'Unfollow',
    feed: 'Recent activity',
    aucunEvent: 'No recent activity. Start by following some members!',
    chargement: 'Loading…',
    erreur: 'Error loading feed.',
    precedent: '← Previous',
    suivant: 'Next →',
    page: 'Page',
    eventTypes: {
      observation_published: 'published an observation',
      observation_validated: 'validated an observation',
      hunt_published: 'shared a hunt',
    },
    species: 'Species',
    category: 'Category',
    categories: {
      migrateur: 'Migratory',
      sedentaire: 'Resident',
      spot_peche: 'Fishing',
      etat_zone: 'Zone status',
    },
    erreurSuivi: 'Unable to follow this user.',
    erreurDesuivi: 'Unable to unfollow.',
    idInvalide: 'Enter a valid numeric ID.',
  },
};

const EVENT_ICONS = {
  observation_published: '🦌',
  observation_validated: '✅',
  hunt_published: '🏕️',
};

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page: { minHeight: '100vh', background: 'var(--couleur-fond)', color: 'var(--couleur-texte)' },
  content: { maxWidth: 880, margin: '0 auto', paddingBottom: '4rem' },
  title: { fontSize: '1.6rem', fontWeight: 700, marginBottom: '0.3rem' },
  sousTitre: { color: 'var(--couleur-texte-attenué)', fontSize: '0.9rem', marginBottom: '1.5rem' },

  // Paywall
  paywall: {
    maxWidth: 520,
    margin: '5rem auto',
    textAlign: 'center',
    background: 'rgba(201,162,39,0.07)',
    border: '1px solid rgba(201,162,39,0.25)',
    borderRadius: 12,
    padding: '2.5rem 2rem',
  },
  paywallIcon: { fontSize: '3rem', marginBottom: '1rem' },
  paywallTitle: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: 'var(--couleur-or)',
    marginBottom: '0.8rem',
  },
  paywallDesc: {
    color: 'var(--couleur-texte-attenué)',
    lineHeight: 1.6,
    marginBottom: '1.5rem',
    fontSize: '0.95rem',
  },
  paywallBtn: {
    padding: '0.8rem 1.8rem',
    background: 'var(--couleur-or)',
    color: '#000',
    borderRadius: 8,
    fontWeight: 700,
    cursor: 'pointer',
    border: 'none',
    fontSize: '0.95rem',
  },

  // Sections
  section: {
    background: 'var(--couleur-fond)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    padding: '1.2rem',
    marginBottom: '1.5rem',
  },
  sectionTitle: { fontSize: '1rem', fontWeight: 700, marginBottom: '1rem' },

  // Follow input row
  followRow: { display: 'flex', gap: '0.6rem', marginBottom: '1rem' },
  input: {
    flex: 1,
    padding: '0.55rem 0.8rem',
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6,
    color: 'var(--couleur-texte)',
    fontSize: '0.9rem',
  },
  btnFollow: {
    padding: '0.55rem 1.2rem',
    background: 'var(--couleur-or)',
    color: '#000',
    border: 'none',
    borderRadius: 6,
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '0.88rem',
  },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },

  // Member chips
  memberList: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' },
  memberChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 20,
    padding: '0.3rem 0.8rem',
    fontSize: '0.82rem',
  },
  btnUnfollow: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.4)',
    cursor: 'pointer',
    fontSize: '0.75rem',
    padding: '0 0.2rem',
  },

  // Feed events
  event: {
    display: 'flex',
    gap: '0.8rem',
    alignItems: 'flex-start',
    padding: '0.9rem 0',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  eventIcon: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    flexShrink: 0,
    background: 'rgba(201,162,39,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.1rem',
  },
  eventBody: { flex: 1 },
  eventActor: { fontWeight: 600, fontSize: '0.9rem' },
  eventDesc: { color: 'var(--couleur-texte-attenué)', fontSize: '0.85rem' },
  eventMeta: { fontSize: '0.75rem', color: 'var(--couleur-texte-attenué)', marginTop: '0.2rem' },
  eventBadge: {
    display: 'inline-block',
    padding: '0.1rem 0.5rem',
    background: 'rgba(201,162,39,0.15)',
    color: 'var(--couleur-or)',
    borderRadius: 4,
    fontSize: '0.72rem',
    fontWeight: 600,
    marginLeft: '0.4rem',
  },

  // Pagination
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    marginTop: '1.2rem',
  },
  btnPage: {
    padding: '0.4rem 0.9rem',
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6,
    color: 'var(--couleur-texte)',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  btnPageDisabled: { opacity: 0.4, cursor: 'default' },

  errMsg: { color: 'var(--couleur-texte-erreur)', fontSize: '0.82rem', marginTop: '0.4rem' },
  muted: {
    color: 'var(--couleur-texte-attenué)',
    fontSize: '0.88rem',
    padding: '1rem 0',
    textAlign: 'center',
  },
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function SocialFeed() {
  const { lang } = useContext(LangContext);
  const { utilisateur } = useAuth();
  const navigate = useNavigate();
  const t = T[lang];
  const annule = useRef(false);

  const [subscriptionLevel, setSubscriptionLevel] = useState(
    utilisateur?.subscription_tier || 'standard'
  );
  const isPremium = ['premium', 'premium_plus', 'pro'].includes(subscriptionLevel);

  // Follow input
  const [followInput, setFollowInput] = useState('');
  const [followLoading, setFollowLoading] = useState(false);
  const [followError, setFollowError] = useState('');

  // Followed members list
  const [members, setMembers] = useState([]);

  // Feed
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedError, setFeedError] = useState('');

  useEffect(
    () => () => {
      annule.current = true;
    },
    []
  );

  // Load real subscription level
  useEffect(() => {
    api
      .get('/me/credits')
      .then((r) => {
        if (!annule.current && r.data?.subscription_tier) {
          setSubscriptionLevel(r.data.subscription_tier);
        }
      })
      .catch(() => {});
  }, []);

  // Load followed members
  const loadMembers = useCallback(async () => {
    try {
      const r = await api.get('/social/following');
      if (!annule.current) setMembers(r.data.users || []);
    } catch {}
  }, []);

  // Load feed
  const loadFeed = useCallback(
    async (p) => {
      setFeedLoading(true);
      setFeedError('');
      try {
        const r = await api.get('/social/feed', { params: { page: p, limit: 20 } });
        if (!annule.current) {
          setEvents(r.data.events || []);
          setTotal(r.data.total || 0);
          setPages(r.data.pages || 1);
        }
      } catch {
        if (!annule.current) setFeedError(t.erreur);
      } finally {
        if (!annule.current) setFeedLoading(false);
      }
    },
    [t.erreur]
  );

  useEffect(() => {
    if (isPremium) {
      loadMembers();
      loadFeed(page);
    }
  }, [isPremium, page, loadMembers, loadFeed]);

  async function handleFollow(e) {
    e.preventDefault();
    setFollowError('');
    const targetId = parseInt(followInput.trim(), 10);
    if (!targetId || isNaN(targetId)) {
      setFollowError(t.idInvalide);
      return;
    }
    setFollowLoading(true);
    try {
      await api.post(`/social/follow/${targetId}`);
      setFollowInput('');
      loadMembers();
      loadFeed(1);
      setPage(1);
    } catch (err) {
      const msg = err?.response?.data?.error || t.erreurSuivi;
      if (!annule.current) setFollowError(msg);
    } finally {
      if (!annule.current) setFollowLoading(false);
    }
  }

  async function handleUnfollow(memberId) {
    try {
      await api.delete(`/social/follow/${memberId}`);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      loadFeed(1);
      setPage(1);
    } catch {}
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // ── Paywall ────────────────────────────────────────────────────────────────
  if (!isPremium) {
    return (
      <PrivateLayout>
        <div style={S.content}>
          <div style={S.paywall}>
            <div style={S.paywallIcon}>👥</div>
            <h2 style={S.paywallTitle}>{t.paywallTitre}</h2>
            <p style={S.paywallDesc}>{t.paywallDesc}</p>
            <button style={S.paywallBtn} onClick={() => navigate('/my-credits')}>
              {t.paywallBtn}
            </button>
          </div>
        </div>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout>
      <div style={S.content}>
        <PageHeader icon="👥" title={t.title} subtitle={t.sousTitre} />

        {/* Follow a member */}
        <div style={S.section}>
          <h2 style={S.sectionTitle}>{t.membres}</h2>

          <form onSubmit={handleFollow} style={S.followRow}>
            <input
              style={S.input}
              type="number"
              min="1"
              value={followInput}
              onChange={(e) => setFollowInput(e.target.value)}
              placeholder={t.chercherMembre}
            />
            <button
              type="submit"
              style={{ ...S.btnFollow, ...(followLoading ? S.btnDisabled : {}) }}
              disabled={followLoading}
            >
              {followLoading ? t.enCours : t.suivre}
            </button>
          </form>
          {followError && <p style={S.errMsg}>{followError}</p>}

          {members.length === 0 ? (
            <p style={S.muted}>{t.aucunSuivi}</p>
          ) : (
            <div style={S.memberList}>
              {members.map((m) => (
                <div key={m.id} style={S.memberChip}>
                  <span>👤 {m.name}</span>
                  <button
                    style={S.btnUnfollow}
                    onClick={() => handleUnfollow(m.id)}
                    title={t.seDesabonner}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Feed */}
        <div style={S.section}>
          <h2 style={S.sectionTitle}>
            {t.feed} {total > 0 && `(${total})`}
          </h2>

          {feedLoading && <p style={S.muted}>{t.chargement}</p>}
          {feedError && (
            <p style={{ ...S.muted, color: 'var(--couleur-texte-erreur)' }}>{feedError}</p>
          )}

          {!feedLoading && !feedError && events.length === 0 && (
            <p style={S.muted}>{t.aucunEvent}</p>
          )}

          {events.map((ev) => {
            const payload = ev.payload || {};
            const typeText = t.eventTypes[ev.event_type] || ev.event_type;
            const icon = EVENT_ICONS[ev.event_type] || '📌';
            const catLabel = payload.category
              ? t.categories[payload.category] || payload.category
              : null;

            return (
              <div key={ev.id} style={S.event}>
                <div style={S.eventIcon}>{icon}</div>
                <div style={S.eventBody}>
                  <div style={S.eventActor}>
                    {ev.actor_name}
                    <span style={{ fontWeight: 400, color: 'var(--couleur-texte-attenué)' }}>
                      {' '}
                      {typeText}
                    </span>
                    {catLabel && <span style={S.eventBadge}>{catLabel}</span>}
                  </div>
                  {payload.species && (
                    <div style={S.eventDesc}>
                      {t.species} : {payload.species}
                    </div>
                  )}
                  <div style={S.eventMeta}>{formatDate(ev.created_at)}</div>
                </div>
              </div>
            );
          })}

          {pages > 1 && (
            <div style={S.pagination}>
              <button
                style={{ ...S.btnPage, ...(page <= 1 ? S.btnPageDisabled : {}) }}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                {t.precedent}
              </button>
              <span style={{ fontSize: '0.85rem', color: 'var(--couleur-texte-attenué)' }}>
                {t.page} {page} / {pages}
              </span>
              <button
                style={{ ...S.btnPage, ...(page >= pages ? S.btnPageDisabled : {}) }}
                disabled={page >= pages}
                onClick={() => setPage((p) => p + 1)}
              >
                {t.suivant}
              </button>
            </div>
          )}
        </div>
      </div>
    </PrivateLayout>
  );
}
